import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMemo, useState } from 'react';
import { MdOutlineSendTimeExtension } from 'react-icons/md';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { usePostalCodeContext } from '../../../contexts/PostalCodeProvider';
import useCitiesActions from '../../../hooks/APIHooks/users/useCitiesActions';
import usePostalCodesActions from '../../../hooks/APIHooks/users/usePostalCodesActions';
import useProvincesActions from '../../../hooks/APIHooks/users/useProvincesActions';
import { addEditPostalCodeSchema } from '../../../schemas';
import { SelectData } from '../../../types/common';
import {
  PostalCodeReqBody,
  PostalCodeTableData,
} from '../../../types/postalCodes';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import PostalCodeModalTable from '../../SetupSubPageComps/PostalCodesComps/PostalCodeModalTable';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const AddPostalCodeModal = ({
  handleModalClose,
}: {
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [selectedProvince, setSelectedProvince] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );
  const [addedPostalCodes, setAddedPostalCodes] = useState<
    Array<PostalCodeTableData>
  >([]);

  // integration of context hooks here
  const { postalCodeList } = usePostalCodeContext();
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { addNewPostalCodeHandler } = usePostalCodesActions('');
  const { getProvinceListHandler } = useProvincesActions('provinceList');
  const { getProvinceWiseCitiesHandler } = useCitiesActions(
    '',
    selectedProvince.id !== -1 ? selectedProvince.id : undefined
  );

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // formatting province dropdown options here
  const provinceOptions = useMemo(() => {
    if (!getProvinceListHandler.data?.is_success) return [];

    return getProvinceListHandler.data.data.map((province) => ({
      id: province.id,
      name: province.province_name,
      code: province.province_code,
    }));
  }, [getProvinceListHandler.data]);

  // formatting province dropdown option descriptions here
  const provinceDescriptionOptions = useMemo(() => {
    if (!getProvinceListHandler.data?.is_success) return [];

    return getProvinceListHandler.data.data.map(
      (province) => `(${province.country_name})`
    );
  }, [getProvinceListHandler.data]);

  // formatting city dropdown options here
  const cityOptions = useMemo(() => {
    if (!getProvinceWiseCitiesHandler.data?.is_success) return [];

    return getProvinceWiseCitiesHandler.data.data
      .filter(
        (city) =>
          !postalCodeList.some(
            (postalCode) =>
              postalCode.province_name === city.province_name &&
              postalCode.city === city.id
          )
      )
      .map((city) => ({
        id: city.id,
        name: city.city_name,
      }));
  }, [getProvinceWiseCitiesHandler.data, postalCodeList]);

  // formatting city dropdown descriptions here
  const cityDescriptionOptions = useMemo(() => {
    if (!getProvinceWiseCitiesHandler.data?.is_success) return [];

    return getProvinceWiseCitiesHandler.data.data
      .filter(
        (city) =>
          !postalCodeList.some(
            (postalCode) =>
              postalCode.province_name === city.province_name &&
              postalCode.city === city.id
          )
      )
      .map((city) => `(${city.province_name})`);
  }, [getProvinceWiseCitiesHandler.data, postalCodeList]);

  const isCityDropdownDisabled = useMemo(() => {
    if (!getProvinceWiseCitiesHandler.data?.is_success) return true;

    return !getProvinceWiseCitiesHandler.data.data.filter(
      (city) =>
        !postalCodeList.some(
          (postalCode) =>
            postalCode.province_name === city.province_name &&
            postalCode.city === city.id
        )
    ).length;
  }, [getProvinceWiseCitiesHandler.data, postalCodeList]);

  const addPostalCodeHandler = async () => {
    try {
      setIsSubmitting(true);

      // sorting postal codes by city id here
      const postalCodeMap = new Map<number, PostalCodeReqBody>();

      for (const { city, postal_code, created_by } of addedPostalCodes) {
        if (!postalCodeMap.has(city.id)) {
          postalCodeMap.set(city.id, {
            city: city.id,
            postal_codes: [],
            created_by,
          });
        }

        postalCodeMap.get(city.id)!.postal_codes.push(postal_code);
      }

      console.log(
        'POSTAL CODE CREATE BODY: ',
        Array.from(postalCodeMap.values())
      );

      const body: Array<PostalCodeReqBody> = Array.from(postalCodeMap.values());

      await addNewPostalCodeHandler
        .mutateAsync({ body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Postal Codes Added!!');

            handleModalClose();
            queryClient.invalidateQueries({ queryKey: ['postal-code-list'] });
          } else {
            toast.error(res.message || 'Postal Codes Adding Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsSubmitting(false);
    }
  };

  // function to add postal code to list declared here
  const addPostalCodesToList = (values: any, actions: FormikHelpers<any>) => {
    const postalCode: PostalCodeTableData = {
      province: values.province,
      city: values.city,
      postal_code: values.postal_code,
      created_by: currentUser?.username as string,
    };

    setAddedPostalCodes((state) => [...state, postalCode]);

    actions.setFieldValue('postal_code', '');
  };

  // function to remove postal code from list declared here
  const removeAddedPostalCode = (postalCode: string) => {
    setAddedPostalCodes((state) =>
      state.filter((code) => code.postal_code !== postalCode)
    );
  };

  // rendering add/edit postal code modal component here
  return (
    <ParentModal onClick={handleModalClose} customWidthClass='md:w-[600px]'>
      <ModalTitle titleText='add postal code' />

      <Formik
        initialValues={{
          province: SELECT_FIELD_DEFAULT_VALUE,
          city: SELECT_FIELD_DEFAULT_VALUE,
          postal_code: '',
        }}
        validationSchema={addEditPostalCodeSchema}
        onSubmit={addPostalCodesToList}
      >
        {(props) => (
          <Form className='space-y-4'>
            <AcademiaFormSelect
              name='province'
              label='Select Province'
              options={provinceOptions}
              optionDescription={provinceDescriptionOptions}
              defaultValue={props.values.province}
              setValueOnChange={(province) => setSelectedProvince(province)}
              showSearchInput
              isRequired
            />

            <AcademiaFormSelect
              name='city'
              label='Select City'
              options={cityOptions}
              optionDescription={cityDescriptionOptions}
              defaultValue={props.values.city}
              showSearchInput
              isDisabled={isCityDropdownDisabled}
              isRequired
            />

            <AcademiaInputField
              type='text'
              name='postal_code'
              label='Postal Code'
              placeholder='Enter postal code here...'
              isRequired
            />

            {addedPostalCodes.length ? (
              <PostalCodeModalTable
                addedPostalCodeList={addedPostalCodes}
                removeAddedPostalCode={removeAddedPostalCode}
              />
            ) : null}

            <div className='grid grid-cols-2 gap-4'>
              <AcademiaButton
                title={
                  props.values.city.id === -1 || !props.values.postal_code
                    ? 'Please, select a city & add a postal first!'
                    : ''
                }
                type='submit'
                variant='info'
                icon={<TbSquareRoundedPlusFilled className='size-6' />}
                isDisabled={
                  props.values.city.id === -1 || !props.values.postal_code
                }
                fullWidth
              >
                Add Postal Code
              </AcademiaButton>

              <AcademiaButton
                title={
                  !addedPostalCodes.length
                    ? 'Please, add some postal codes first!'
                    : ''
                }
                type='button'
                onClick={addPostalCodeHandler}
                variant='premium'
                icon={<MdOutlineSendTimeExtension className='size-6' />}
                isLoading={isSubmitting}
                isDisabled={!addedPostalCodes.length}
                fullWidth
              >
                Submit
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default AddPostalCodeModal;
